import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const situations = [
  { value: "think-damage", label: "I think my roof has storm damage" },
  { value: "know-damage", label: "I know I have storm damage" },
  { value: "insurance-claim", label: "I'm working through a claim" },
  { value: "repair-options", label: "I need repair options" },
  { value: "replacement-options", label: "I need replacement options" },
];

export default function LeadForm({ compact = false }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    situation: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Required";
    if (!formData.phone.trim()) newErrors.phone = "Required";
    if (!formData.email.trim()) newErrors.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email";
    if (!formData.address.trim()) newErrors.address = "Required";
    if (!formData.city.trim()) newErrors.city = "Required";
    if (!formData.situation) newErrors.situation = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Lead submission error:", err);
      }
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="lead-form-success"
        className={`text-center ${compact ? 'py-8' : 'bg-white border border-gray-200 rounded-xl p-12'}`}
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-navy mb-3">
          Thank You!
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Your information has been received. A member of our team will reach out
          within 24 hours to discuss your situation.
        </p>
      </div>
    );
  }

  // Compact version for hero
  if (compact) {
    return (
      <form data-testid="lead-capture-form-compact" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Input
              data-testid="lead-form-name"
              placeholder="Your Name *"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={`h-12 ${errors.name ? "border-red-500" : ""}`}
            />
          </div>
          <div>
            <Input
              data-testid="lead-form-phone"
              type="tel"
              placeholder="Phone *"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className={`h-12 ${errors.phone ? "border-red-500" : ""}`}
            />
          </div>
        </div>

        <Input
          data-testid="lead-form-email"
          type="email"
          placeholder="Email *"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          className={`h-12 ${errors.email ? "border-red-500" : ""}`}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            data-testid="lead-form-address"
            placeholder="Property Address *"
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
            className={`h-12 ${errors.address ? "border-red-500" : ""}`}
          />
          <Input
            data-testid="lead-form-city"
            placeholder="City *"
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
            className={`h-12 ${errors.city ? "border-red-500" : ""}`}
          />
        </div>

        <Select
          value={formData.situation}
          onValueChange={(value) => handleChange("situation", value)}
        >
          <SelectTrigger
            data-testid="lead-form-situation"
            className={`h-12 ${errors.situation ? "border-red-500" : ""}`}
          >
            <SelectValue placeholder="What's your situation? *" />
          </SelectTrigger>
          <SelectContent>
            {situations.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="submit"
          data-testid="lead-form-submit"
          disabled={loading}
          className="w-full bg-orange hover:bg-orange-hover text-white h-14 text-base rounded-lg font-medium"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Phone className="w-5 h-5 mr-2" />
              Get Connected Free
            </>
          )}
        </Button>

        <p className="text-center text-gray-500 text-xs">
          No obligation. Free service. We respect your privacy.
        </p>
      </form>
    );
  }

  // Full version
  return (
    <form
      data-testid="lead-capture-form"
      onSubmit={handleSubmit}
      className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg shadow-navy/5"
    >
      <div className="space-y-5">
        {/* Row 1: Name and Phone */}
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="name" className="text-navy font-medium mb-2 block">
              Name *
            </Label>
            <Input
              id="name"
              data-testid="lead-form-name-full"
              placeholder="Your full name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={`h-12 ${errors.name ? "border-red-500" : ""}`}
            />
            {errors.name && (
              <span className="text-red-500 text-sm mt-1">{errors.name}</span>
            )}
          </div>

          <div>
            <Label htmlFor="phone" className="text-navy font-medium mb-2 block">
              Phone *
            </Label>
            <Input
              id="phone"
              data-testid="lead-form-phone-full"
              type="tel"
              placeholder="(555) 555-5555"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className={`h-12 ${errors.phone ? "border-red-500" : ""}`}
            />
            {errors.phone && (
              <span className="text-red-500 text-sm mt-1">{errors.phone}</span>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="text-navy font-medium mb-2 block">
            Email *
          </Label>
          <Input
            id="email"
            data-testid="lead-form-email-full"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className={`h-12 ${errors.email ? "border-red-500" : ""}`}
          />
          {errors.email && (
            <span className="text-red-500 text-sm mt-1">{errors.email}</span>
          )}
        </div>

        {/* Row 2: Address and City */}
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="address" className="text-navy font-medium mb-2 block">
              Property Address *
            </Label>
            <Input
              id="address"
              data-testid="lead-form-address-full"
              placeholder="123 Main Street"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              className={`h-12 ${errors.address ? "border-red-500" : ""}`}
            />
            {errors.address && (
              <span className="text-red-500 text-sm mt-1">{errors.address}</span>
            )}
          </div>

          <div>
            <Label htmlFor="city" className="text-navy font-medium mb-2 block">
              City *
            </Label>
            <Input
              id="city"
              data-testid="lead-form-city-full"
              placeholder="Minneapolis"
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
              className={`h-12 ${errors.city ? "border-red-500" : ""}`}
            />
            {errors.city && (
              <span className="text-red-500 text-sm mt-1">{errors.city}</span>
            )}
          </div>
        </div>

        {/* Situation Dropdown */}
        <div>
          <Label className="text-navy font-medium mb-2 block">
            Describe Your Situation *
          </Label>
          <Select
            value={formData.situation}
            onValueChange={(value) => handleChange("situation", value)}
          >
            <SelectTrigger
              data-testid="lead-form-situation-full"
              className={`h-12 ${errors.situation ? "border-red-500" : ""}`}
            >
              <SelectValue placeholder="Select your situation" />
            </SelectTrigger>
            <SelectContent>
              {situations.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.situation && (
            <span className="text-red-500 text-sm mt-1">{errors.situation}</span>
          )}
        </div>

        {/* Message (optional) */}
        <div>
          <Label htmlFor="message" className="text-navy font-medium mb-2 block">
            Additional Details (Optional)
          </Label>
          <Textarea
            id="message"
            data-testid="lead-form-message"
            placeholder="Any additional information about your situation..."
            value={formData.message}
            onChange={(e) => handleChange("message", e.target.value)}
            rows={3}
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          data-testid="lead-form-submit-full"
          disabled={loading}
          className="w-full bg-orange hover:bg-orange-hover text-white h-14 text-base rounded-lg font-medium mt-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Get Connected
            </>
          )}
        </Button>

        <p className="text-center text-gray-500 text-sm">
          No obligation. Free service. We respect your privacy.
        </p>
      </div>
    </form>
  );
}
